import type { ReactElement } from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';
import type { TrailItem } from '../../navigation/trail';

interface TrailProps {
  items: TrailItem[];
}

/**
 * "You are here": section › subject › page, under the top bar.
 *
 * The last item is the page that is open, so it is plain text and carries
 * aria-current. An item without an address (a subject heading) is plain text too.
 * Draws nothing for an empty trail, so an address the menu cannot place shows no row.
 */
export default function Trail({ items }: TrailProps): ReactElement | null {
  if (items.length === 0) return null;
  return (
    <nav aria-label="You are here" className="min-w-0">
      <ol className="flex flex-wrap items-center gap-x-1.5 gap-y-0.5 text-sm">
        {items.map((item, i) => {
          const isLast = i === items.length - 1;
          return (
            <li key={`${i}-${item.label}`} className="flex min-w-0 items-center gap-1.5">
              {i > 0 && <ChevronRight className="h-3.5 w-3.5 flex-shrink-0 text-gray-300" aria-hidden="true" />}
              {isLast ? (
                <span aria-current="page" className="truncate font-semibold text-gray-900">{item.label}</span>
              ) : item.to ? (
                <Link
                  to={item.to}
                  className="truncate rounded font-medium text-gray-500 transition-colors hover:text-primary-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
                >
                  {item.label}
                </Link>
              ) : (
                <span className="truncate text-gray-500">{item.label}</span>
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}
